import type { UseFormReturnType } from '@mantine/form';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';
import type { z } from 'zod';

import { getCommissionerChannelHandleAvailability } from '~/services/commissioner/commissioner-service.service.api';
import { useUserSchema } from '~/domains/auth/hooks/useUserSchema';

// Custom hook for debouncing values
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}

type UseCheckHandleAvailabilityProps<T extends { handle: string }> = {
  enabled?: boolean;
  form: UseFormReturnType<T>;
};

export const useCheckHandleAvailability = <T extends { handle: string }>({
  enabled = true,
  form,
}: UseCheckHandleAvailabilityProps<T>) => {
  const { t } = useTranslation('channel');
  const [isHandleTaken, setIsHandleTaken] = useState(false);
  const { signUpSchema } = useUserSchema();
  const previousHandleRef = useRef<string | null>(null);
  const previousDebouncedValueRef = useRef<string | null>(null);

  const handleValue = form.values.handle;
  // Use our custom debounce hook to get the debounced handle value
  const debouncedHandleValue = useDebounce(handleValue, 600);

  // Clear error when debounced value changes
  useEffect(() => {
    if (
      debouncedHandleValue !== previousDebouncedValueRef.current &&
      debouncedHandleValue !== previousHandleRef.current
    ) {
      // Clear any existing handle taken error when the debounced value changes
      if (isHandleTaken) {
        setIsHandleTaken(false);
        form.clearFieldError('handle');
      }
      previousDebouncedValueRef.current = debouncedHandleValue;
    }
  }, [debouncedHandleValue, form, isHandleTaken]);

  // Check handle availability when the debounced handle value changes
  useEffect(() => {
    // Only check availability if:
    // 1. The hook is enabled
    // 2. There's a handle value
    // 3. The handle field is dirty (has been modified)
    // 4. The debounced handle value has actually changed from the previous value
    if (
      enabled &&
      debouncedHandleValue &&
      form.isDirty('handle') &&
      debouncedHandleValue !== previousHandleRef.current
    ) {
      // Update the previous handle value
      previousHandleRef.current = debouncedHandleValue;

      const trimmedHandleValue = debouncedHandleValue.trim();

      // Check for validation errors first
      const result = signUpSchema.shape.handle.safeParse(trimmedHandleValue);
      if (!result.success && 'error' in result) {
        setIsHandleTaken(false);
        form.setFieldError('handle', result.error.errors[0].message);
        return;
      }

      // Make the API call
      getCommissionerChannelHandleAvailability(trimmedHandleValue)
        .then(({ data }) => {
          if (!data.isAvailable) {
            setIsHandleTaken(true);
            form.setFieldError('handle', t('validation.handleTaken'));
          } else {
            setIsHandleTaken(false);
            // Don't clear the error here, let the validate function handle it
          }
        })
        .catch((error) => {
          console.error('Error checking handle availability:', error);
        });
    }
  }, [enabled, debouncedHandleValue, form, signUpSchema, t]);

  return { isHandleTaken };
};
